/**
 * CRM Feature Constants
 *
 * Stage definitions, colors, and configuration for the sales pipeline
 */

import type { StageDefinition, PipelineStage, CrmActivityType } from './types';

// =============================================================================
// Pipeline Stage Definitions
// =============================================================================

export const PIPELINE_STAGES: StageDefinition[] = [
  {
    key: 'prospect_acquired',
    label: 'Prospect Acquired',
    labelFr: "Acquisition d'un prospect",
    color: '#6366f1', // Indigo
    bgColor: '#eef2ff',
    order: 1,
    isTerminal: false,
    defaultProbability: 10,
  },
  {
    key: 'first_contact',
    label: 'First Contact',
    labelFr: 'Premier contact',
    color: '#8b5cf6', // Violet
    bgColor: '#f5f3ff',
    order: 2,
    isTerminal: false,
    defaultProbability: 20,
  },
  {
    key: 'discovery_meeting',
    label: 'Discovery Meeting',
    labelFr: 'Rendez-vous de decouverte',
    color: '#06b6d4', // Cyan
    bgColor: '#ecfeff',
    order: 3,
    isTerminal: false,
    defaultProbability: 40,
  },
  {
    key: 'proposal_drafting',
    label: 'Proposal Drafting',
    labelFr: 'Elaboration proposition',
    color: '#f59e0b', // Amber
    bgColor: '#fffbeb',
    order: 4,
    isTerminal: false,
    defaultProbability: 60,
  },
  {
    key: 'proposal_presented',
    label: 'Proposal Presented',
    labelFr: 'Proposition presentee',
    color: '#f97316', // Orange
    bgColor: '#fff7ed',
    order: 5,
    isTerminal: false,
    defaultProbability: 80,
  },
  {
    key: 'quote_created',
    label: 'Quote Created',
    labelFr: 'Devis cree',
    color: '#22c55e', // Green
    bgColor: '#f0fdf4',
    order: 6,
    isTerminal: true,
    transformsTo: 'quote',
    defaultProbability: 100,
  },
  {
    key: 'lost',
    label: 'Lost',
    labelFr: 'Perdu',
    color: '#ef4444', // Red
    bgColor: '#fef2f2',
    order: 99,
    isTerminal: true,
    defaultProbability: 0,
  },
];

/**
 * Map for quick stage lookups by key.
 *
 * Built lazily below: includes the legacy universal stages AND every
 * pipeline-specific stage (ds_*, rr_*, pt_*) defined in `PIPELINES`.
 * Without the pipeline-specific entries, drag-drop silently bails out on
 * tender / direct-sales / recurring-revenue opportunities because
 * `getStageDefinition()` returns undefined.
 */
export const STAGE_MAP = new Map<PipelineStage, StageDefinition>(
  PIPELINE_STAGES.map((s) => [s.key, s])
);

/**
 * Get stage definition by key
 */
export function getStageDefinition(stage: PipelineStage): StageDefinition | undefined {
  return STAGE_MAP.get(stage);
}

/**
 * Stages shown as columns in Kanban board (excludes 'lost' which is handled separately)
 */
export const KANBAN_STAGES = PIPELINE_STAGES.filter((s) => s.key !== 'lost');

/**
 * Non-terminal stages that opportunities can be moved to via normal drag-drop
 * Terminal stages (quote_created, lost) require special handling
 */
export const ACTIVE_STAGES = PIPELINE_STAGES.filter((s) => !s.isTerminal);

// =============================================================================
// Activity Type Definitions
// =============================================================================

export interface ActivityTypeDefinition {
  key: CrmActivityType;
  label: string;
  labelFr: string;
  icon: string; // Lucide icon name
  color: string;
  suggestedDuration: number; // minutes
}

export const ACTIVITY_TYPES: ActivityTypeDefinition[] = [
  {
    key: 'call',
    label: 'Phone Call',
    labelFr: 'Appel telephonique',
    icon: 'Phone',
    color: '#3b82f6', // Blue
    suggestedDuration: 15,
  },
  {
    key: 'meeting',
    label: 'Meeting',
    labelFr: 'Reunion',
    icon: 'Users',
    color: '#8b5cf6', // Violet
    suggestedDuration: 60,
  },
  {
    key: 'email',
    label: 'Email',
    labelFr: 'Email',
    icon: 'Mail',
    color: '#06b6d4', // Cyan
    suggestedDuration: 15,
  },
  {
    key: 'discovery_session',
    label: 'Discovery Session',
    labelFr: 'Rendez-vous decouverte',
    icon: 'Search',
    color: '#f59e0b', // Amber
    suggestedDuration: 90,
  },
  {
    key: 'proposal_presentation',
    label: 'Proposal Presentation',
    labelFr: 'Presentation proposition',
    icon: 'Presentation',
    color: '#f97316', // Orange
    suggestedDuration: 60,
  },
  {
    key: 'note',
    label: 'Note',
    labelFr: 'Note',
    icon: 'StickyNote',
    color: '#6b7280', // Gray
    suggestedDuration: 0,
  },
];

/**
 * Map for quick activity type lookups
 */
export const ACTIVITY_TYPE_MAP = new Map<CrmActivityType, ActivityTypeDefinition>(
  ACTIVITY_TYPES.map((a) => [a.key, a])
);

/**
 * Get activity type definition by key
 */
export function getActivityTypeDefinition(
  type: CrmActivityType
): ActivityTypeDefinition | undefined {
  return ACTIVITY_TYPE_MAP.get(type);
}

// =============================================================================
// Opportunity Type Definitions
// =============================================================================

export interface OpportunityTypeDefinition {
  key: string;
  label: string;
  labelFr: string;
  color: string;
}

export const OPPORTUNITY_TYPES: OpportunityTypeDefinition[] = [
  {
    key: 'new_business',
    label: 'New Business',
    labelFr: 'Nouvelle affaire',
    color: '#22c55e', // Green
  },
  {
    key: 'expansion',
    label: 'Expansion',
    labelFr: 'Expansion',
    color: '#3b82f6', // Blue
  },
  {
    key: 'upgrade',
    label: 'Upgrade',
    labelFr: 'Mise a niveau',
    color: '#8b5cf6', // Violet
  },
  {
    key: 'cross-sell',
    label: 'Cross-Sell',
    labelFr: 'Vente croisee',
    color: '#f59e0b', // Amber
  },
  {
    key: 'renewal',
    label: 'Renewal',
    labelFr: 'Renouvellement',
    color: '#06b6d4', // Cyan
  },
  {
    key: 'tender',
    label: 'Public Tender',
    labelFr: 'Appel d\'offres',
    color: '#ec4899', // Pink
  },
  {
    key: 'other',
    label: 'Other',
    labelFr: 'Autre',
    color: '#6b7280', // Gray
  },
];

// =============================================================================
// Pipeline Definitions (maps to HubSpot)
// =============================================================================

export interface PipelineDefinition {
  key: string;
  label: string;
  labelFr: string;
  hubspotId: string;
  color: string;
  types: string[]; // opportunity types that belong to this pipeline
  stages: Array<{
    key: string;
    label: string;
    labelFr: string;
    probability: number;
    isClosed: boolean;
    isWon: boolean;
  }>;
}

export const PIPELINES: PipelineDefinition[] = [
  {
    key: 'direct_sales',
    label: 'Direct Sales',
    labelFr: 'Ventes directes',
    hubspotId: 'default',
    color: '#22c55e',
    types: ['new_business', 'other'],
    stages: [
      { key: 'ds_leads', label: 'Leads', labelFr: 'Prospects', probability: 0.2, isClosed: false, isWon: false },
      { key: 'ds_meetings', label: 'Meetings', labelFr: 'Rencontres', probability: 0.4, isClosed: false, isWon: false },
      { key: 'ds_demo', label: 'Demo', labelFr: 'Demo', probability: 0.6, isClosed: false, isWon: false },
      { key: 'ds_quotes', label: 'Quotes', labelFr: 'Devis', probability: 0.8, isClosed: false, isWon: false },
      { key: 'ds_won', label: 'Won', labelFr: 'Gagne', probability: 1.0, isClosed: true, isWon: true },
      { key: 'ds_invoiced', label: 'Won & Invoiced', labelFr: 'Gagne et facture', probability: 1.0, isClosed: true, isWon: true },
      { key: 'ds_lost', label: 'Lost', labelFr: 'Perdu', probability: 0, isClosed: true, isWon: false },
    ],
  },
  {
    key: 'recurring_revenue',
    label: 'Recurring Revenue',
    labelFr: 'Revenus recurrents',
    hubspotId: '709524393',
    color: '#3b82f6',
    types: ['expansion', 'upgrade', 'cross-sell', 'renewal'],
    stages: [
      { key: 'rr_contacted', label: 'Customer Contacted', labelFr: 'Client contacte', probability: 0.2, isClosed: false, isWon: false },
      { key: 'rr_appointment', label: 'Appointment Scheduled', labelFr: 'Rendez-vous planifie', probability: 0.5, isClosed: false, isWon: false },
      { key: 'rr_negotiation', label: 'Contract Sent', labelFr: 'Contrat envoye', probability: 0.7, isClosed: false, isWon: false },
      { key: 'rr_awaiting_po', label: 'Awaiting PO', labelFr: 'En attente du PO', probability: 0.9, isClosed: false, isWon: false },
      { key: 'rr_won', label: 'Won', labelFr: 'Gagne', probability: 1.0, isClosed: true, isWon: true },
      { key: 'rr_lost', label: 'Lost', labelFr: 'Perdu', probability: 0, isClosed: true, isWon: false },
    ],
  },
  {
    key: 'public_tender',
    label: 'Public Tender',
    labelFr: 'Appels d\'offres',
    hubspotId: '10784175',
    color: '#ec4899',
    types: ['tender'],
    stages: [
      { key: 'pt_decided', label: 'Decided to Go', labelFr: 'Decision de soumissionner', probability: 0.05, isClosed: false, isWon: false },
      { key: 'pt_budget', label: 'Budget Approved', labelFr: 'Budget approuve', probability: 0.1, isClosed: false, isWon: false },
      { key: 'pt_writing', label: 'Writing Process', labelFr: 'Redaction en cours', probability: 0.15, isClosed: false, isWon: false },
      { key: 'pt_published', label: 'Published', labelFr: 'Publie', probability: 0.3, isClosed: false, isWon: false },
      { key: 'pt_submitted', label: 'Submitted', labelFr: 'Soumis', probability: 0.5, isClosed: false, isWon: false },
      { key: 'pt_selected', label: 'Selected (Lowest Bidder)', labelFr: 'Selectionne', probability: 0.7, isClosed: false, isWon: false },
      { key: 'pt_demo', label: 'Demo', labelFr: 'Demo', probability: 0.8, isClosed: false, isWon: false },
      { key: 'pt_contract', label: 'Contract Sent', labelFr: 'Contrat envoye', probability: 0.9, isClosed: false, isWon: false },
      { key: 'pt_won', label: 'Won', labelFr: 'Gagne', probability: 1.0, isClosed: true, isWon: true },
      { key: 'pt_lost', label: 'Lost', labelFr: 'Perdu', probability: 0, isClosed: true, isWon: false },
    ],
  },
];

export const PIPELINE_MAP = new Map(PIPELINES.map((p) => [p.key, p]));

// Register pipeline-specific stage definitions (ds_*, rr_*, pt_*) into STAGE_MAP
// so getStageDefinition() + isTransitionAllowed() work for all pipeline types.
for (const pipeline of PIPELINES) {
  for (let i = 0; i < pipeline.stages.length; i += 1) {
    const s = pipeline.stages[i]!;
    if (STAGE_MAP.has(s.key as PipelineStage)) continue;
    STAGE_MAP.set(s.key as PipelineStage, {
      key: s.key as PipelineStage,
      label: s.label,
      labelFr: s.labelFr,
      color: pipeline.color,
      bgColor: '#f9fafb',
      order: i + 1,
      isTerminal: s.isClosed,
      defaultProbability: Math.round(s.probability * 100),
    });
  }
}

export function getPipelineForType(type: string): PipelineDefinition {
  return PIPELINES.find((p) => p.types.includes(type)) || PIPELINES[0]!;
}

// =============================================================================
// Priority Definitions
// =============================================================================

export interface PriorityDefinition {
  key: string;
  label: string;
  labelFr: string;
  color: string;
  bgColor: string;
}

export const PRIORITIES: PriorityDefinition[] = [
  {
    key: 'high',
    label: 'High',
    labelFr: 'Haute',
    color: '#ef4444',
    bgColor: '#fef2f2',
  },
  {
    key: 'medium',
    label: 'Medium',
    labelFr: 'Moyenne',
    color: '#f59e0b',
    bgColor: '#fffbeb',
  },
  {
    key: 'low',
    label: 'Low',
    labelFr: 'Basse',
    color: '#22c55e',
    bgColor: '#f0fdf4',
  },
];

// =============================================================================
// Default Values
// IMPORTANT: These values should be kept in sync with:
//   apps/rest_server/lib/crm-config.js
// =============================================================================

export const DEFAULT_CURRENCY = 'CAD';

/**
 * Pagination defaults - synced with backend lib/crm-config.js
 */
export const PAGINATION = {
  /** Default page size for opportunity lists */
  OPPORTUNITIES_PAGE_SIZE: 25,
  /** Default page size for pipeline board columns */
  PIPELINE_COLUMN_PAGE_SIZE: 35,
  /** Default page size for activity lists */
  ACTIVITIES_PAGE_SIZE: 25,
  /** Maximum allowed page size */
  MAX_PAGE_SIZE: 100,
  /** Default days ahead for upcoming activities widget */
  UPCOMING_ACTIVITIES_DAYS: 7,
  /** Default limit for upcoming activities widget */
  UPCOMING_ACTIVITIES_LIMIT: 10,
} as const;

/** @deprecated Use PAGINATION.PIPELINE_COLUMN_PAGE_SIZE instead */
export const DEFAULT_PAGE_SIZE = PAGINATION.PIPELINE_COLUMN_PAGE_SIZE;

// =============================================================================
// Validation Rules
// IMPORTANT: These values should be kept in sync with:
//   apps/rest_server/lib/crm-config.js
// =============================================================================

export const VALIDATION = {
  OPPORTUNITY: {
    ESTIMATED_VALUE_MIN: 0,
    ESTIMATED_VALUE_MAX: 100_000_000, // $100M max
    PROBABILITY_MIN: 0,
    PROBABILITY_MAX: 100,
    TITLE_MAX_LENGTH: 500,
    DESCRIPTION_MAX_LENGTH: 5000,
  },
  ACTIVITY: {
    DURATION_MINUTES_MIN: 1,
    DURATION_MINUTES_MAX: 480, // 8 hours max
    TITLE_MAX_LENGTH: 500,
    DESCRIPTION_MAX_LENGTH: 5000,
    OUTCOME_NOTES_MAX_LENGTH: 2000,
  },
} as const;

// =============================================================================
// Stage Transition Rules
// =============================================================================

/**
 * Terminal stages - cannot transition FROM these.
 * Includes legacy universal keys AND pipeline-specific ds_won/rr_won/pt_won/*_lost/*_invoiced keys.
 */
export const TERMINAL_STAGES: PipelineStage[] = [
  'quote_created', 'lost',
  'ds_won', 'ds_invoiced', 'ds_lost',
  'rr_won', 'rr_lost',
  'pt_won', 'pt_lost',
] as PipelineStage[];

/**
 * Check if a stage transition is allowed.
 * Any non-terminal stage that exists in STAGE_MAP can transition to any other
 * stage that exists in STAGE_MAP (STAGE_MAP includes pipeline-specific stages).
 */
export function isTransitionAllowed(
  fromStage: PipelineStage,
  toStage: PipelineStage
): boolean {
  if (fromStage === toStage) return true;
  if (TERMINAL_STAGES.includes(fromStage)) return false;
  return STAGE_MAP.has(toStage);
}

/**
 * Get next stage in the pipeline (for "Advance" button)
 */
export function getNextStage(currentStage: PipelineStage): PipelineStage | null {
  const current = STAGE_MAP.get(currentStage);
  if (!current || current.isTerminal) return null;

  const nextOrder = current.order + 1;
  const next = PIPELINE_STAGES.find((s) => s.order === nextOrder && !s.isTerminal);
  return next?.key ?? null;
}

// =============================================================================
// Cache Configuration
// =============================================================================

/**
 * TanStack Query stale times for CRM data
 * Centralized to allow easy tuning of cache behavior
 */
export const CACHE_STALE_TIMES = {
  /** Pipeline board data - 2 minutes (frequently changing) */
  PIPELINE_BOARD: 2 * 60 * 1000,
  /** Pipeline summary metrics - 2 minutes */
  PIPELINE_SUMMARY: 2 * 60 * 1000,
  /** Account managers list - 10 minutes (rarely changes) */
  ACCOUNT_MANAGERS: 10 * 60 * 1000,
  /** Activity data - 1 minute (may change during active work) */
  ACTIVITIES: 1 * 60 * 1000,
  /** Upcoming activities - 2 minutes */
  UPCOMING_ACTIVITIES: 2 * 60 * 1000,
} as const;
